// One way to send any video to THE WALL: shrink it on this device when it's bigger than it needs to be (THE WALL's server does it
// if this device can't), send just its sound as a take, pick up dropped uploads, keep the screen awake — per the tick-boxes.
import axios from "axios";
import { API } from "../../App";
import { MB, NoDevice, fitKbps, inspect, shrinkOnDevice, soundOnDevice } from "./device";
import { authCfg, sendParts } from "./parts";
import { stayAwake } from "./awake";

const SERVER_MAX = 4096 * MB;
const base = (name) => name.replace(/\.[^.]+$/, "");
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Where a video can go: the normal upload for that place, its part size and how big a file it takes.
const PLACES = {
  voltbox: (w) => ({ url: `${API}/voltbox/${w.itemId}/video-chunk`, chunk: 8 * MB, limit: 800 * MB, place: { item_id: w.itemId }, fields: (f) => ({ filename: f.name, label: w.label || "" }) }),
  page_media: (w) => ({ url: `${API}/admin/page-media/video-chunk`, chunk: 4 * MB, limit: 300 * MB, hard: 1900 * MB, place: { kind: w.kind, slug: w.slug, title: w.title || "" },
    fields: (f, orig) => ({ kind: w.kind, slug: w.slug, title: w.title ?? base(orig.name), filename: f.name }) }),
  block: (w) => ({ url: `${API}/admin/page-layouts/${encodeURIComponent(w.layoutKey)}/video-chunk`, chunk: 4 * MB, limit: 195 * MB, place: { key: w.layoutKey }, fields: () => ({}) }),
  poster: (w) => ({ url: `${API}/admin/event-posters/${w.eventSlug}/video-chunk`, chunk: 4 * MB, limit: 100 * MB, place: { event_slug: w.eventSlug, caption: w.caption || "" }, fields: () => ({ caption: w.caption || "" }) }),
};

const pctOf = (s) => Math.round((s?.pct || 0) * 100);
export const stageText = (s) => {
  if (!s) return "";
  const p = pctOf(s);
  return { read: "Checking the video…", shrink: `Shrinking on this device · ${p}%`, sound: `Taking out the sound · ${p}%`, send: `Sending · ${p}%`,
    upload: `Sending the original to THE WALL's server · ${p}%`, wait: `Connection dropped — waiting to pick up at ${p}%`,
    server: `${s.note || "Shrinking on THE WALL's server"} · ${p}%` }[s.phase] || "";
};

async function waitJob(jobId, onStage) {
  for (;;) {
    await sleep(2500);
    let j;
    try { j = (await axios.get(`${API}/video-shrink/jobs/${jobId}`, authCfg())).data; }
    catch (e) { if (e.response?.status === 404) throw new Error("THE WALL's server lost that video — please send it again"); continue; }
    if (j.status === "done") return j.result;
    if (j.status === "failed" || j.status === "skipped") throw new Error(j.error || "THE WALL's server couldn't shrink that video");
    onStage({ phase: "server", pct: (j.progress || 0) / 100, note: j.status === "queued" ? "Waiting its turn on THE WALL's server" : j.note });
  }
}

// The device couldn't: send the original to THE WALL's server, which shrinks it (or takes out the sound) and puts it in place.
async function viaServer(file, dest, place, prefs, onStage, soundOnly = false) {
  if (file.size > SERVER_MAX) throw new Error("Videos up to 4 GB — export a smaller copy first");
  const fields = { filename: file.name, dest, place: JSON.stringify(place), size: String(prefs.size), sound_only: soundOnly ? "1" : "" };
  const r = await sendParts({ url: `${API}/video-shrink/chunk`, file, chunk: 8 * MB, fields, resume: prefs.resume,
    onProgress: (pct) => onStage({ phase: "upload", pct }), onWait: (w) => onStage({ phase: "wait", pct: w.pct }) });
  onStage({ phase: "server", pct: 0, note: "Waiting its turn on THE WALL's server" });
  return waitJob(r.job_id, onStage);
}

const direct = (f, P, orig, prefs, onStage) => sendParts({ url: P.url, file: f, chunk: P.chunk, fields: P.fields(f, orig), resume: prefs.resume,
  onProgress: (pct) => onStage({ phase: "send", pct }), onWait: (w) => onStage({ phase: "wait", pct: w.pct }) });

async function sendSound(file, w, prefs, onStage) {
  const label = `${base(file.name)} · camera sound`.slice(0, 120);
  try {
    const info = await inspect(file).catch(() => { throw new NoDevice("can't read it here"); });
    const audio = await soundOnDevice(file, info, { onProgress: (pct) => onStage({ phase: "sound", pct }) });
    const fields = { filename: audio.name, label, version_id: "", finished: "", note: "", tell_band: "" };
    return await sendParts({ url: `${API}/voltbox/${w.itemId}/audio-chunk`, file: audio, chunk: 4 * MB, fields, resume: prefs.resume,
      onProgress: (pct) => onStage({ phase: "send", pct }), onWait: (x) => onStage({ phase: "wait", pct: x.pct }) });
  } catch (e) {
    if (!(e instanceof NoDevice)) throw e;
    return viaServer(file, "voltbox_take", { item_id: w.itemId, label }, prefs, onStage, true);
  }
}

// where = { type: "voltbox", itemId } | { type: "page_media", kind, slug, title? } | { type: "block", layoutKey } | { type: "poster", eventSlug, caption? }
export async function sendVideo(file, where, prefs, onStage = () => {}) {
  const P = PLACES[where.type](where);
  const release = prefs.awake ? await stayAwake() : () => {};
  let drop = () => {};
  try {
    if (where.type === "voltbox" && prefs.soundOnly) return await sendSound(file, where, prefs, onStage);
    onStage({ phase: "read", pct: 0 });
    const info = await inspect(file).catch(() => null);
    const tooBig = file.size > P.limit;
    const needs = prefs.shrink && (tooBig || (!!info && Math.min(info.width, info.height) > prefs.size));
    if (!needs) {
      if (file.size > (P.hard || P.limit)) throw new Error(`This video is ${Math.round(file.size / MB)} MB — more than ${Math.round(P.limit / MB)} MB fits here. Tick “Shrink larger videos” and send it again.`);
      return await direct(file, P, file, prefs, onStage);
    }
    try {
      if (!info) throw new NoDevice("can't read it here");
      const limit = P.hard && fitKbps(prefs.size, P.limit, info.duration, 320) < 1500 ? P.hard : P.limit; // long talks may go bigger where that's allowed
      const r = await shrinkOnDevice(file, info, { size: prefs.size, limit, onProgress: (pct) => onStage({ phase: "shrink", pct }) });
      drop = r.drop;
      return await direct(r.file, P, file, prefs, onStage);
    } catch (e) {
      if (!(e instanceof NoDevice)) throw e;
      return await viaServer(file, where.type, P.place, prefs, onStage);
    }
  } finally {
    drop();
    release();
  }
}
